import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../primitives/Icon';
import BaseDrawer from '../Drawer/BaseDrawer';
import Button from '../../primitives/Button';
import './MenuDrawer.css';
import { useWouterNavigate } from '../../../../hooks/useWouterNavigation';
import { useAuth } from '../../../../contexts/AuthContext';

interface OrganizerMenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const OrganizerMenuDrawer: React.FC<OrganizerMenuDrawerProps> = ({
  isOpen,
  onClose
}) => {
  const navigate = useWouterNavigate();
  const { logout } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Handle logout with confirmation
  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const performLogout = async () => {
    try {
      setShowLogoutConfirm(false);
      onClose();
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  // Handle Help modal - close drawer first, then show modal
  const handleHelpClick = () => {
    onClose(); // Close the drawer first
    setTimeout(() => setShowHelpModal(true), 150); // Then open modal after drawer animation
  };

  const closeHelpModal = useCallback(() => {
    setShowHelpModal(false);
  }, []);

  // Handle ESC key to close Help modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showHelpModal) {
        e.preventDefault();
        closeHelpModal();
      }
    };

    if (showHelpModal) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [showHelpModal, closeHelpModal]);

  return (
    <>
    <BaseDrawer
      isOpen={isOpen}
      onClose={onClose}
      className="menu-drawer menu-drawer--organizer"
    >
      <div className="menu-drawer-header">
        <h2>Menu</h2>
      </div>
      <div className="menu-drawer-content menu-drawer-content--organizer">
        {/* Public Space — points to /events (tourist app entry), not / (landing) */}
        <button
          className="menu-item"
          onClick={() => {
            onClose();
            setTimeout(() => navigate('/events', { replace: true }), 100);
          }}
        >
          <Icon name="Home" size={24} />
          <span>Public Space</span>
        </button>

        {/* Help - opens modal */}
        <button
          className="menu-item"
          onClick={handleHelpClick}
        >
          <Icon name="CircleHelp" size={24} />
          <span>Help</span>
        </button>

        {/* Feedback */}
        <button
          className="menu-item"
          onClick={() => {
            onClose();
            window.open("https://forms.gle/WsLPhinC1fS5eYJcA", "_blank");
          }}
        >
          <Icon name="MessageSquare" size={24} />
          <span>Feedback</span>
        </button>

        {/* Divider before About / Legal & Logout */}
        <div className="menu-drawer-divider" />

        {/* About */}
        <button
          className="menu-item"
          onClick={() => {
            onClose();
            setTimeout(() => navigate('/about'), 100);
          }}
        >
          <Icon name="Info" size={24} />
          <span>About</span>
        </button>

        {/* Legal & Privacy */}
        <button
          className="menu-item"
          onClick={() => {
            onClose();
            setTimeout(() => navigate('/legal'), 100);
          }}
        >
          <Icon name="Shield" size={24} />
          <span>Legal & Privacy</span>
        </button>

        {/* Logout Confirmation - above logout button */}
        {showLogoutConfirm && (
          <div className="menu-drawer-logout-confirm">
            <p>Are you sure you want to log out?</p>
            <div className="menu-drawer-logout-confirm__actions">
              <Button variant="ghost" size="sm" onClick={cancelLogout}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={performLogout}>
                Logout
              </Button>
            </div>
          </div>
        )}

        {/* Logout Button */}
        <button
          className="menu-item menu-item--logout"
          onClick={handleLogoutClick}
        >
          <Icon name="LogOut" size={24} />
          <span>Logout</span>
        </button>
      </div>

    </BaseDrawer>

      {/* Help Modal - Rendered via Portal to escape drawer constraints */}
      {showHelpModal && createPortal(
        <div className="help-modal-overlay" onClick={closeHelpModal}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <button className="help-modal__close" onClick={closeHelpModal} type="button">
              <Icon name="X" size={20} />
            </button>

            <h2 className="help-modal__title">How can we help?</h2>

            <div className="help-modal__options">
              <button
                className="help-modal__option"
                onClick={() => {
                  closeHelpModal();
                  // Dispatch event to start product tour
                  setTimeout(() => window.dispatchEvent(new CustomEvent('startProductTour')), 150);
                }}
                type="button"
              >
                <div className="help-modal__option-icon help-modal__option-icon--tour">
                  <Icon name="Navigation" size={24} />
                </div>
                <div className="help-modal__option-content">
                  <span>Take a Tour</span>
                  <p>Discover all the features of your dashboard</p>
                </div>
              </button>

              <button
                className="help-modal__option help-modal__option--disabled"
                type="button"
                disabled
              >
                <div className="help-modal__option-icon help-modal__option-icon--faq">
                  <Icon name="BookOpen" size={24} />
                </div>
                <div className="help-modal__option-content">
                  <span>How it works <em>(Coming soon)</em></span>
                  <p>Learn how to get the most out of What 2 Do</p>
                </div>
              </button>
            </div>

            <div className="help-modal__divider" />

            <div className="help-modal__contact">
              <p>Need help? Reach out to us!</p>
              <div className="help-modal__contact-buttons">
                <Button
                  variant="primary"
                  size="md"
                  className="help-modal__contact-btn help-modal__contact-btn--whatsapp"
                  onClick={() => { window.open("https://wa.me/33771006946", "_blank"); closeHelpModal(); }}
                >
                  <Icon name="MessageCircle" size={20} />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="md"
                  className="help-modal__contact-btn help-modal__contact-btn--email"
                  onClick={() => { window.location.href = "mailto:nico@what2do.today"; closeHelpModal(); }}
                >
                  <Icon name="Mail" size={20} />
                  Email
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default OrganizerMenuDrawer; 